// src/data/news.js
//
// ─────────────────────────────────────────────────────────────────────────
// THE WIRE — industry news data (s69)
// ─────────────────────────────────────────────────────────────────────────
//
// Single source of truth for the homepage Wire section, the /news hub
// page, the News Desk contributor's bibliography, and the sitemap entry
// for each post. Add a new item here, write the matching blog post,
// rebuild — everything else updates.
//
// FIELD SHAPE
//   slug          : routes to /blog/<slug>; must match the blog post's
//                   frontmatter slug exactly
//   headline      : the Wire-card and hub-card headline. Short.
//   summary       : 1–2 sentence Wire summary. Distinct from the blog
//                   post's excerpt — this is for the card, not SEO.
//   category      : Wire taxonomy. "release" | "research" | "industry"
//                   | "policy" | "people". Drives the colored tag chip.
//   publishedDate : ISO date (YYYY-MM-DD). Matches the blog post's
//                   publishedDate. Used for chronological sort.
//   source        : { name, url } of the original publication. Used in
//                   card attribution (text only, NO outbound link from
//                   the Wire — the outbound link belongs at the bottom
//                   of the blog post as a source credit).
//   voice         : editorial voice tag. "opinionated" | "neutral" |
//                   "product". Not rendered on the card; used by the
//                   hub page to badge each entry so the mix stays
//                   visible at a glance.
//
// ROTATION WORKFLOW — adding a new Wire item:
//   1. Find a real, dated story from one of our monitored sources
//      (see src/pages/Sources.jsx and contributors/wwg-news-desk).
//   2. Write a 400–600 word blog post at
//        src/blog/news-wire/<NN>-<slug>.md
//      using category: "industry" and the news-desk author block.
//      End the post with the standard source-credit block.
//   3. Prepend a new entry to NEWS_ITEMS (newest first).
//   4. Rebuild. The post auto-appears in:
//        • homepage Wire (top 6)
//        • /news hub (all of them)
//        • /blog (filtered to Industry)
//        • sitemap.xml (via the blog-frontmatter glob)
//
// CATEGORY LABELS — Wire-card taxonomy. Kept short for chip rendering.
export const CATEGORY_LABELS = {
  release:   'RELEASE',
  research:  'RESEARCH',
  industry:  'INDUSTRY',
  policy:    'POLICY',
  people:    'PEOPLE',
};

// Color hint for the category chip on the Wire card. Maps to the
// existing theme tokens — sienna for the loud ones, sage for the
// quieter editorial classes, ink for neutral material.
//   sienna  = #B5532A (the site's accent)
//   sage    = #9CA89A
//   inkSoft = #5A5A52
// WireSection consumes these names and resolves them against the theme
// at render time so a future dark-mode toggle on the homepage would
// pick up the right shade.
export const CATEGORY_TONES = {
  release:   'sienna',
  research:  'sage',
  industry:  'sienna',
  policy:    'inkSoft',
  people:    'sage',
};

// ─────────────────────────────────────────────────────────────────────────
// THE WIRE — newest first.
// Six items is the homepage cap. The /news hub shows everything in this
// array, so this list can grow past six without changing any code.
// ─────────────────────────────────────────────────────────────────────────
export const NEWS_ITEMS = [
  {
    slug: 'vitro-shawn-winn-western-regional-sales-manager-2026',
    headline: 'Vitro promotes Shawn Winn to lead Western regional sales',
    summary:
      'A people-news note from the Solarban side of the trade: Vitro Architectural Glass moves a four-year Central Plains commercial account manager up to run the Western desk.',
    category: 'people',
    publishedDate: '2026-05-13',
    source: { name: 'Glass Magazine', url: 'https://www.glassmagazine.com/news/2026/vitro-promotes-shawn-winn-western-regional-sales-manager' },
    voice: 'neutral',
  },
  {
    slug: 'two-hantavirus-cases-cleaning-industry-visibility-2026',
    headline: 'Two presumptive hantavirus cases revive the question of who actually cleans',
    summary:
      'Reported this week and following a much-publicized cruise-ship outbreak earlier in the spring: the cleaning trade keeps being the invisible variable in the public-health story. Worth thinking about.',
    category: 'industry',
    publishedDate: '2026-05-12',
    source: { name: 'Cleaning & Maintenance Management', url: 'https://cmmonline.com/news/2-americans-presumably-positive-for-the-hantavirus' },
    voice: 'opinionated',
  },
  {
    slug: 'asthma-awareness-month-2026-pollen-pure-water',
    headline: 'Asthma Awareness Month is a reminder that windows are an allergen reservoir',
    summary:
      'May is Asthma Awareness Month, and the indoor-air-quality story keeps lapping back to a thing we already write a lot about: pollen, mold, and the role of the exterior glass in carrying it indoors.',
    category: 'research',
    publishedDate: '2026-05-11',
    source: { name: 'Cleaning & Maintenance Management', url: 'https://cmmonline.com/news/asthma-awareness-month-draws-attention-to-allergens' },
    voice: 'product',
  },
  {
    slug: 'issa-cleaning-technician-registered-apprenticeship-2026',
    headline: 'The federal apprenticeship for cleaning is here. What it means for window-cleaning operators.',
    summary:
      "ISSA's Department of Labor-approved Cleaning Technician Registered Apprenticeship is the first national framework that treats cleaning as a skilled trade. Window cleaning sits inside that umbrella — uncomfortably, and usefully.",
    category: 'policy',
    publishedDate: '2026-05-04',
    source: { name: 'Cleaning & Maintenance Management', url: 'https://cmmonline.com/news/issa-launches-registered-apprenticeship-program' },
    voice: 'opinionated',
  },
  {
    slug: 'skyline-color-water-decorative-glass-2026',
    headline: 'Skyline\'s Color + Water decorative glass is another reason to identify substrate first',
    summary:
      "A new printed-and-tempered decorative line from a Chicago studio: watercolor-derived art, scaled large, on glass intended for partitions and feature walls. Another piece of stock the trade is going to be asked to clean without knowing what's on the surface.",
    category: 'release',
    publishedDate: '2026-05-04',
    source: { name: 'Glass Magazine', url: 'https://www.glassmagazine.com/news/2026/skyline-announces-new-decorative-glass-collaboration' },
    voice: 'product',
  },
  {
    slug: 'vitro-ecoarmor-protective-topcoat-2026',
    headline: 'Vitro\'s EcoArmor topcoat: a factory coating that survives tempering, headed for the field',
    summary:
      'Vitro launched a protective full-surface topcoat on its Solarban 90 low-e glass that stays on the lite through tempering and reportedly cuts cycle times. The pipe between this and what a route-cleaning operator sees on a finished facade is short.',
    category: 'release',
    publishedDate: '2026-04-30',
    source: { name: 'Glass Magazine', url: 'https://www.glassmagazine.com/news/2026/vitro-architectural-glass-introduces-protective-topcoat' },
    voice: 'neutral',
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Helpers — kept tiny because consumers do their own filtering/slicing.
// ─────────────────────────────────────────────────────────────────────────

// Newest first, capped at `limit`. Used by the homepage Wire (limit 6).
export function getRecentNews(limit = 6) {
  return [...NEWS_ITEMS]
    .sort((a, b) => (b.publishedDate || '').localeCompare(a.publishedDate || ''))
    .slice(0, limit);
}

// All items, newest first. Used by the /news hub.
export function getAllNews() {
  return [...NEWS_ITEMS]
    .sort((a, b) => (b.publishedDate || '').localeCompare(a.publishedDate || ''));
}

// Lookup by slug — used by the news-desk contributor page bibliography
// and by any future related-news widget.
export function getNewsItemBySlug(slug) {
  return NEWS_ITEMS.find((n) => n.slug === slug) || null;
}
